import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { RosterstreamError } from 'libs/domain';

import { RootService } from '@app/src/app/minimal/services/RootService';
import { SentryService } from '@app/src/app/minimal/services/Sentry/Sentry.service';
import { SystemAlertSnackBarService } from '@app/src/app/shared/components/system-alert-snackbar';
import { RosterstreamService } from '@app/src/app/shared/services/auth/sso/rosterstream.service';

import { LandingRoute } from '../../../../constants';
import { ROSTERSTREAM_MESSAGES } from '../../constants/landing-rosterstream.constants';

@Component({
  selector: 'mg-rosterstream-callback',
  templateUrl: './rosterstream-callback.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RosterstreamCallbackComponent implements OnInit {
  constructor(
    private _rosterstreamService: RosterstreamService,
    private _route: ActivatedRoute,
    private _rootService: RootService,
    private _router: Router,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
    private _sentry: SentryService,
  ) {}

  async ngOnInit(): Promise<void> {
    this._rootService.addLoadingPromise(this._handleCallback());
  }

  private async _handleCallback(): Promise<void> {
    const urlParams = this._route.snapshot.queryParams;

    const code = urlParams.code || '';
    const state = urlParams.state || '';
    const error = urlParams.error || '';
    const provider = urlParams.provider || '';

    try {
      this._rosterstreamService.onSsoCallbackReturn();
      // Rosterstream oauth error
      if (error) {
        return this._handleError(error);
      }

      if (!code) {
        return this._handleError(RosterstreamError.CODE_REQUIRED);
      }

      // validate state param - only if we have one (classlink does not send it when logging in from their portal)
      // this should be safe still because rosterstream only allows 1 use of each code. 
      if (state && !this._rosterstreamService.validateState(state)) {
        return this._handleError(RosterstreamError.INVALID_STATE);
      }
      

      // Exchange code for token and login
      const response = await this._rosterstreamService.login(code);

      if (response.success) {
        this._router.navigate(['/']);
      } else {
        const { reason, hash } = response;
        this._handleError((hash as RosterstreamError) || RosterstreamError.UNKNOWN_ERROR, {
          message: reason,
          provider,
        });
      }
    } catch (error) {
      this._handleError(RosterstreamError.UNKNOWN_ERROR, { error, provider });
    }
  }

  private async _handleError(
    code: RosterstreamError,
    context?: {
      provider: string;
      message?: string;
      error?: any;
    },
  ): Promise<void> {
    const { message = ROSTERSTREAM_MESSAGES.AUTH_ERROR_GENERIC, error, provider } = context || {};

    console.log('rosterstream error', context);

    this._systemAlertSnackBar.error(message);
    this._router.navigate([`/${LandingRoute.ROOT}/${LandingRoute.EMAIL}`]);
    this._sentry.captureMessageAsError(`Rosterstream login error`, {
      code,
      message,
      error,
      provider,
    });
  }
}
